import { useEffect, useMemo, useState } from "react";
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable
} from "material-react-table";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../app/rootReducer";
import { useSelector } from "../../../app/helpers";
import { commonLocalization } from "../../../functions/tableLocalization";
import TableDeleteConfirmDialog from "../../Common/TableDeletionConfirmDialog/TableDeletionConfirmDialog.component";
import { useRemoteFetchAdminLocations } from "../../../hooks/Remote/Location/useRemoteFetchAdminLocations";
import { LocationInventory } from "../../../features/FloorManager/typings/location-inventory";
import {
  CateringServiceRow,
  CateringServiceRowValues
} from "./Domain/Types/FacilityService-row.types";
import {
  editCateringImage,
  renderCateringPricePerUnitCell,
  SlidePanelCreateEditService
} from "./catering-list.partial";
import { mapUpdatedRowToPartialCateringItem } from "./helpers/mapUpdatedRowToPartialCateringItem.function";
import { mapNewRowToPartialCateringItem } from "./helpers/mapNewRowToPartialCateringItem.function";
import { cateringRowValuesToFormData, checkCateringVal } from "./functions/catering-list.functions";
import { Stakeholder } from "../../../features/FloorManager/typings/permission.entity";
import { useRemoteFetchCompanies } from "../../../hooks/Remote/Companies/useRemoteFetchCompanies";
import { useRemoteFetchCompanyUnits } from "../../../hooks/Remote/CompanyUnits/useRemoteFetchCompanyUnits";
import { useRemoteFetchUserInfoForAllUsers } from "../../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { useRemoteFetchAllCateringServices } from "../../../hooks/Remote/Services/CateringMenu/useRemoteFetchAllCateringServices";
import { useRemoteDeleteCateringMenu } from "../../../hooks/Remote/Services/CateringMenu/useRemoteDeleteCateringMenu";
import { useRemoteUpdateCateringMenu } from "../../../hooks/Remote/Services/CateringMenu/useRemoteUpdateCateringMenu";
import { useRemoteCreateCateringMenu } from "../../../hooks/Remote/Services/CateringMenu/useRemoteCreateCateringMenu";
import { validateCateringRow } from "./functions/catering-list-validation.function";
import { useSnackbar } from "notistack";
import { EnlargedImageDialog } from "../../../features/EnlargedImageCarousel/enlarged-image-dialog.component";
import { FileObject } from "../../../features/FileDropzone/typings/FileObject.type";
import {
  editPermissionSelect,
  renderPermissionCell,
  renderSimpleCellName,
  renderTableItemImage
} from "../../Common/ListTablePartial/ListTablePartial.partial";
import { renderMasterBookingLeadTimeCell } from "../../EquipmentList/FilterableEquipmentList/filterable-equipment-list.partial";
import { ListRowActions } from "../../Common/ListTablePartial/List-row-actions.partial";
import { ListTopToolbar } from "../../Common/ListTablePartial/List-top-toolbar.partial";
import { withSeeditMrtInitialState } from "../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../themes/use-seedit-mrt-table-options";

/**
 * @version 0.1.0
 */
export default function CateringSerivceList() {
  const {
    userInformation: { sub }
  } = useSelector((state: RootState) => state.login);
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [data, setData] = useState<CateringServiceRow[]>();

  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletionSelected, setDeletionSelected] = useState<number[] | null>(null);
  const [imageFile, setImageFile] = useState<{ file: FileObject[] }>({ file: [] });
  const [showBiggerImage, setShowBiggerImage] = useState<string | null>(null);
  const [deleteRowImage, setDeleteRowImage] = useState<{
    rowId: number | null;
    picturePath: string | null;
  }>({ rowId: null, picturePath: null });

  const {
    data: remoteCateringMenu,
    isLoading: isRemoteCateringMenuLoading,
    refetch: refetchRemoteCateringMenu
  } = useRemoteFetchAllCateringServices();

  const { mutateAsync: removeCaterings, status: removeCateringStatus } =
    useRemoteDeleteCateringMenu();
  const { mutateAsync: addCaterings, status: addCateringStatus } = useRemoteCreateCateringMenu();
  const { mutateAsync: updateCaterings, status: updateCateringStatus } =
    useRemoteUpdateCateringMenu();

  const { data: locations } = useRemoteFetchAdminLocations({ mode: "admin" });

  const { data: remoteCompanies } = useRemoteFetchCompanies();
  const { data: remoteDepartments } = useRemoteFetchCompanyUnits(Stakeholder.CostCenterUnit);
  const { data: remoteAllUsers } = useRemoteFetchUserInfoForAllUsers();

  const column = useMemo<MRT_ColumnDef<CateringServiceRow>[]>(
    () => [
      {
        accessorKey: "picturePath",
        header: t("Image"),
        enableEditing: true,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) =>
          renderTableItemImage(
            cell.row.original.picturePath,
            "catering-item",
            100,
            setShowBiggerImage
          ),
        Edit: ({ column, row }) =>
          editCateringImage({
            title: t("Upload an catering image"),
            header: column.columnDef.header,
            rowOriginal: row.original,
            imageFile,
            setImageFile,
            setDeleteRowImage
          }),
        size: 100
      },
      {
        accessorKey: "name",
        header: t("Name"),
        enableEditing: true,
        size: 200,
        muiEditTextFieldProps: {
          onFocus: () =>
            /* istanbul ignore next */
            setValidationErrors({
              ...validationErrors,
              name: undefined
            }),
          onChange: e => {
            checkCateringVal(e.currentTarget.value, "name", validationErrors, setValidationErrors);
          },
          helperText: validationErrors?.name,
          required: true,
          error: !!validationErrors?.name
        }
      },
      {
        size: 400,
        enableEditing: true,
        accessorKey: "description",
        header: t("Description"),
        muiEditTextFieldProps: {
          onFocus: () =>
            /* istanbul ignore next */
            setValidationErrors({
              ...validationErrors,
              description: undefined
            }),
          onChange: e => {
            checkCateringVal(
              e.currentTarget.value,
              "description",
              validationErrors,
              setValidationErrors
            );
          },
          helperText: validationErrors?.description,
          required: true,
          error: !!validationErrors?.description
        }
      },
      {
        header: t("Price/Unit"),
        accessorKey: "pricePerUnit",
        size: 50,
        Cell: ({ row: { original } }) => renderCateringPricePerUnitCell(original.pricePerUnit),
        muiEditTextFieldProps: {
          required: true,
          type: "number",
          error: !!validationErrors?.pricePerUnit,
          helperText: validationErrors?.pricePerUnit,
          onFocus: () =>
            /* istanbul ignore next */
            setValidationErrors({
              ...validationErrors,
              pricePerUnit: undefined
            }),
          onChange: e =>
            checkCateringVal(
              e.currentTarget.value,
              "pricePerUnit",
              validationErrors,
              setValidationErrors
            )
        }
      },
      {
        accessorKey: "location.name",
        header: t("Location"),
        Cell: ({ row: { original } }) => renderSimpleCellName(original.location?.name),
        editSelectOptions: ({ row }) => {
          const locNull = { name: t("No location"), value: null };
          const arrLoc = locations || [
            { id: row.original.location?.id, name: row.original.location?.name }
          ];
          const locOpts = [locNull, ...arrLoc];
          return locOpts.map(loc => ({ label: loc.name, value: loc.name, key: loc.name }));
        },
        size: 200,
        editVariant: "select"
      },
      {
        accessorKey: "servicePermissions",
        header: t("Permissions"),
        size: 200,
        editVariant: "select",
        enableEditing: true,
        Edit: props =>
          editPermissionSelect({
            permRow: props.row.original,
            remoteCompanies,
            remoteDepartments,
            remoteAllUsers
          }),
        Cell: ({ row: { original } }) =>
          renderPermissionCell(original, remoteCompanies, remoteDepartments, remoteAllUsers)
      },
      {
        header: t("_cateringManagement_bookingLeadTime"),
        accessorKey: "bookingLeadTime",
        size: 220,
        enableEditing: true,
        Cell: ({ row: { original } }) => renderMasterBookingLeadTimeCell(original.bookingLeadTime),
        muiEditTextFieldProps: {
          type: "number",
          inputProps: { min: 0, step: 0.5 },
          placeholder: t("Booking Lead Time"),
          error: !!validationErrors?.bookingLeadTime,
          helperText: validationErrors?.bookingLeadTime,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              bookingLeadTime: undefined
            }),
          onChange: e => {
            const { value } = e.currentTarget;
            setValidationErrors({
              ...validationErrors,
              bookingLeadTime: value === "" ? undefined : validationErrors?.bookingLeadTime
            });
          }
        }
      }
    ],
    [
      validationErrors,
      i18n.language,
      locations,
      imageFile,
      remoteCompanies,
      remoteDepartments,
      remoteAllUsers
    ]
  );

  useEffect(() => {
    if (!remoteCateringMenu || !locations) return;

    setData(
      remoteCateringMenu.map(item => ({
        id: item.id || remoteCateringMenu.length - 1,
        name: item.name,
        description: item.description,
        pricePerUnit: item.pricePerUnit,
        picturePath: item.picturePath,
        location: locations.find(loc => loc.id === item.locationInventoryId),
        servicePermissions: item.servicePermissions,
        bookingLeadTime: item.bookingLeadTime ?? null,
        affectedCompanies: item.servicePermissions?.companyIds,
        affectedDepartments: item.servicePermissions?.costCenterUnitIds,
        affectedUsers: item.servicePermissions?.userIds
      }))
    );
  }, [remoteCateringMenu, locations]);

  // after deleting a row, refresh the list
  useEffect(() => {
    if (removeCateringStatus === "success") {
      const multiDeleted =
        deletionSelected && deletionSelected.length > 1
          ? t("_deletedMultiCatering")
          : t("_deletedSingleCatering");

      enqueueSnackbar(t("Successfully deleted catering", { unit: multiDeleted }), {
        variant: "success"
      });
      setIsDeleteModalOpen(false);
      refetchRemoteCateringMenu();
      setDeletionSelected(null);
    }
  }, [removeCateringStatus]);

  // after adding a eow, refresh the list
  useEffect(() => {
    if (addCateringStatus === "success") refetchRemoteCateringMenu();
  }, [addCateringStatus]);

  // after adding a row, refresh the list
  useEffect(() => {
    if (updateCateringStatus === "success") refetchRemoteCateringMenu();
  }, [updateCateringStatus]);

  const resetTableCreateEdit = () => {
    setValidationErrors({});
    setImageFile({ file: [] });
  };

  const updateCateringLocation = (
    values: CateringServiceRowValues,
    locations: LocationInventory[] | undefined
  ) => {
    const upd = values;
    const selectedLoc = locations?.find(loc => values["location.name"] === loc.name);

    upd["location"] = selectedLoc;
    upd["location.id"] = selectedLoc?.id;
    upd["location.name"] = values["location.name"];

    return upd;
  };

  const updateCateringPermissions = (
    values: CateringServiceRowValues,
    rowOriginal: CateringServiceRow
  ) => {
    const upd = values;

    upd["affectedCompanies"] = rowOriginal.affectedCompanies;
    upd["affectedDepartments"] = rowOriginal.affectedDepartments;
    upd["affectedUsers"] = rowOriginal.affectedUsers;
    upd["servicePermissions"] = {
      id: values["id"],
      companyIds: rowOriginal.affectedCompanies,
      costCenterUnitIds: rowOriginal.affectedDepartments,
      userIds: rowOriginal.affectedUsers
    };

    return upd;
  };

  const updateCateringPicturepath = (
    values: CateringServiceRowValues,
    rowOriginal: CateringServiceRow
  ) => {
    const upd = values;

    if (deleteRowImage.rowId === rowOriginal.id && deleteRowImage.picturePath === null) {
      upd["picturePath"] = undefined;
    }

    return upd;
  };

  /** deletes caterings */
  const handleRowDelete = (rowUids: number[]) => {
    removeCaterings({ ids: [...rowUids] });
    table.resetRowSelection();
  };

  /** adds a catering */
  const handleRowAdd: MRT_TableOptions<CateringServiceRow>["onCreatingRowSave"] = async ({
    row,
    values,
    exitCreatingMode
  }) => {
    const newValidationErrors = validateCateringRow(values);

    if (Object.values(newValidationErrors).some(Boolean)) {
      return setValidationErrors(newValidationErrors);
    }

    resetTableCreateEdit();

    const locUpdated = updateCateringLocation(values, locations);
    const permUpdated = updateCateringPermissions(locUpdated, row.original);
    const pictureUpdated = updateCateringPicturepath(permUpdated, row.original);
    const newVal = mapNewRowToPartialCateringItem(pictureUpdated);

    const newValueFormData = cateringRowValuesToFormData(newVal, row.original, sub, imageFile);

    exitCreatingMode();
    await addCaterings(newValueFormData);
    table.setCreatingRow(null); //exit creating mode
    setImageFile({ file: [] });
    setDeleteRowImage({ rowId: null, picturePath: null });
  };

  /** updates a catering */
  const handleRowUpdate: MRT_TableOptions<CateringServiceRow>["onEditingRowSave"] = async ({
    row,
    values,
    exitEditingMode
  }) => {
    const newValidationErrors = validateCateringRow(values);
    if (Object.values(newValidationErrors).some(Boolean)) {
      return setValidationErrors(newValidationErrors);
    }

    resetTableCreateEdit();

    const locUpdated = updateCateringLocation(values, locations);
    const permUpdated = updateCateringPermissions(locUpdated, row.original);
    const pictureUpdated = updateCateringPicturepath(permUpdated, row.original);
    const newVal = mapUpdatedRowToPartialCateringItem(pictureUpdated, row.original);

    const newValueFormData = cateringRowValuesToFormData(newVal, row.original, sub, imageFile);

    exitEditingMode();
    await updateCaterings(newValueFormData);
    table.setEditingRow(null); // exit editing mode
    setImageFile({ file: [] });
    setDeleteRowImage({ rowId: null, picturePath: null });
  };
  const seeditMrtOptions = useSeeditMrtTableOptions<CateringServiceRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns: column ?? [],
    data: data ?? [],
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableRowSelection: true,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    enableEditing: true,
    autoResetPageIndex: false,
    getRowId: row => row.id?.toString(),
    onCreatingRowCancel: resetTableCreateEdit,
    onCreatingRowSave: handleRowAdd,
    onEditingRowCancel: resetTableCreateEdit,
    onEditingRowSave: handleRowUpdate,
    state: {
      isLoading: isRemoteCateringMenuLoading,
      isSaving: (addCateringStatus || updateCateringStatus || removeCateringStatus) === "loading",
      showProgressBars: isRemoteCateringMenuLoading
    },
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 }
    }),
    positionToolbarAlertBanner: "none",
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 15, 20, 30],
      variant: "outlined"
    },
    paginationDisplayMode: "pages",
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("There are no catering")
    },
    renderRowActions: ({ row, table }) => {
      return (
        <ListRowActions
          title={"catering"}
          row={row as MRT_Row<any>}
          table={table as MRT_TableInstance<any>}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          setDeletionSelected={setDeletionSelected}
        />
      );
    },
    renderTopToolbar: ({ table }) => {
      return (
        <ListTopToolbar
          table={table}
          title={t("Catering")}
          createBtnLabel={t("_addCatering")}
          disabledDeleteButton={table.getSelectedRowModel().flatRows.length <= 0}
          onDelete={() => {
            setIsDeleteModalOpen(true);

            const selected = table.getSelectedRowModel().rows.map(row => row.original.id);
            setDeletionSelected(selected);
          }}
        />
      );
    },
    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
      <SlidePanelCreateEditService
        table={table}
        row={row}
        internalEditComponents={internalEditComponents}
        headline={t("_addCatering")}
      />
    ),
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => (
      <SlidePanelCreateEditService
        internalEditComponents={internalEditComponents}
        headline={t("_editCatering")}
        table={table}
        row={row}
      />
    )
  });

  return (
    <>
      <MaterialReactTable table={table} />

      {/* Confirm Deletion Modal */}
      <TableDeleteConfirmDialog
        onConfirm={() => {
          /* istanbul ignore next */
          if (deletionSelected?.length) handleRowDelete(deletionSelected);
        }}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        dialogDesc={
          deletionSelected?.length === 1
            ? t("_tableSingleDeletionConfirmationDescNeutral", {
                title: t("_deleteConfirmCatering")
              })
            : t("_tableDeletionConfirmationDesc")
        }
        deleteStatus={removeCateringStatus}
      />

      <EnlargedImageDialog
        open={!!showBiggerImage}
        picturePath={showBiggerImage}
        handleClose={() => setShowBiggerImage(null)}
      />
    </>
  );
}
